import { CornerUpLeft, MapPin, Megaphone, Smartphone, User } from 'lucide-react';
import type { Message, MessageAttachment } from '../../api/types';
import { flowRowsOf, locationOf, waOf } from '../../lib/whatsappMessage';

/**
 * Bubble pieces for the message shapes only WhatsApp produces. Everything here
 * exists because the data already arrives and the generic bubble had nowhere to put
 * it: a shared vCard rendered as a dead paperclip link, a pinned location rendered as
 * a line of coordinates, and a submitted Flow form rendered as nothing at all.
 */
/** A vCard the contact shared. The phone is the point of it, so make it callable. */
export function ContactAttachment({ attachment }: { attachment: MessageAttachment }) {
  const phone = typeof attachment.meta?.phone === 'string' ? attachment.meta.phone : null;
  return (
    <span className="msg-vcard">
      <User size={13} />
      <span className="msg-vcard-main">
        <span className="msg-vcard-name">{attachment.fileName ?? 'Shared contact'}</span>
        {phone && (
          <a className="msg-vcard-phone" href={`tel:${phone.replace(/[^\d+]/g, '')}`}>
            {phone}
          </a>
        )}
      </span>
    </span>
  );
}

/** A dropped pin. Coordinates are not an address — link out so they can be read. */
export function LocationCard({ message }: { message: Message }) {
  const location = locationOf(message);
  if (!location) return null;
  const label = [location.name, location.address].filter(Boolean).join(', ');
  const href =
    location.url || `https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`;
  return (
    <a className="msg-location" href={href} target="_blank" rel="noreferrer">
      <MapPin size={13} />
      <span className="msg-location-main">
        <span className="msg-location-name">{label || 'Shared location'}</span>
        <span className="msg-location-coords">
          {location.latitude}, {location.longitude}
        </span>
      </span>
    </a>
  );
}

/** A submitted WhatsApp Flow. The answers are the message — the body is just a label. */
export function FlowResponseCard({ message }: { message: Message }) {
  const flow = waOf(message).flowResponse;
  const rows = flowRowsOf(message);
  if (!flow || rows.length === 0) return null;
  return (
    <div className="msg-flow">
      {flow.name && <div className="msg-flow-name">{flow.name}</div>}
      {rows.map(([key, value]) => (
        <div className="msg-flow-row" key={key}>
          <span className="msg-flow-key">{key.replace(/[_-]+/g, ' ')}</span>
          <span className="msg-flow-value">
            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * The message this one replied to. WhatsApp shows the quote inline, so a thread
 * without it reads as a non-sequitur whenever the contact answers an older message.
 */
export function QuotedReply({ quoted }: { quoted: Message }) {
  const text = (quoted.content ?? '').trim();
  return (
    <div className="msg-quote">
      <CornerUpLeft size={11} />
      <span>{text || `${quoted.attachments[0]?.fileType ?? 'message'} attachment`}</span>
    </div>
  );
}

/** Click-to-WhatsApp ad context: which ad or post sent this person here. */
export function ReferralNote({ message }: { message: Message }) {
  const referral = waOf(message).referral;
  if (!referral) return null;
  const headline = referral.headline || referral.body;
  return (
    <a
      className="msg-referral"
      href={referral.source_url || undefined}
      target="_blank"
      rel="noreferrer"
    >
      <Megaphone size={11} />
      <span>Started from an ad{headline ? ` · ${headline}` : ''}</span>
    </a>
  );
}

/** Why a bubble is thin: an unrenderable message, or media we could not fetch. */
export function MessageProblem({ message }: { message: Message }) {
  const wa = waOf(message);
  const mediaError = message.contentAttributes.mediaError;
  if (wa.unsupported) {
    return (
      <div className="msg-problem">
        WhatsApp could not deliver this message’s content — check the WhatsApp app.
      </div>
    );
  }
  if (typeof mediaError === 'string') {
    return <div className="msg-problem">Attachment unavailable — {mediaError}</div>;
  }
  if (wa.error) return <div className="msg-problem">{wa.error}</div>;
  return null;
}

/**
 * Outgoing messages the team sent from somewhere other than this console — the
 * WhatsApp Business app, the Page inbox, the Instagram app. Named per channel,
 * because "sent from the app" points an agent at the wrong app otherwise.
 */
export function EchoBadge({ where = 'WhatsApp Business app' }: { where?: string }) {
  return (
    <span className="msg-echo">
      <Smartphone size={11} />
      {where}
    </span>
  );
}

/**
 * Marks a send that went out as an approved template. Worth calling out: a template
 * is the only thing that reaches a contact outside the 24h window, so whether the
 * last reply was one decides what an agent can do next.
 */
export function TemplateBadge({ message }: { message: Message }) {
  if (!message.contentAttributes.templateParams) return null;
  return <span className="msg-template-tag">Template</span>;
}
